import { createContext, useCallback, useEffect, useReducer } from "react";

import {
  isAuthenticated as authIsAuthenticated,
  logout as authLogout,
  getUser,
  initAuth,
  onAuthChanged,
  startOtp,
  verifyOtp,
} from "../lib/auth";
import { identifyUser, resetUser, trackEvent } from "../utils/analytics";

const INITIALIZE = "INITIALIZE";
const SIGN_OUT = "SIGN_OUT";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    default:
      return state;
  }
};

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    // syncAuth is called both by the onAuthChanged event bus (for subsequent
    // auth changes like logout) and after initAuth resolves (for the initial
    // INITIALIZE dispatch that unblocks AuthGuard).
    const syncToState = () => {
      const user = getUser();
      const authed = authIsAuthenticated();
      if (authed && user) {
        identifyUser({
          ...user,
          firstName: user.first_name,
          lastName: user.last_name,
        });
      }
      dispatch({
        type: INITIALIZE,
        payload: { isAuthenticated: authed, user },
      });
    };

    const handleSignOut = () => {
      dispatch({ type: SIGN_OUT });
    };

    const handleAuthChanged = () => {
      const authed = authIsAuthenticated();
      if (!authed) {
        handleSignOut();
      } else {
        syncToState();
      }
    };

    // Register the auth change listener first so we don't miss events that
    // fire during initAuth (e.g. a successful refresh on cold load).
    const unsubscribe = onAuthChanged(handleAuthChanged);

    // Probe for an existing session. Always dispatches INITIALIZE when done
    // so AuthGuard knows initialization is complete, regardless of outcome.
    initAuth().then(() => syncToState());

    return unsubscribe;
  }, []);

  const startLogin = useCallback(async (email) => {
    return startOtp(email);
  }, []);

  const completeLogin = useCallback(async (email, code, session) => {
    await verifyOtp(email, code, session);
    const user = getUser();
    if (user) {
      identifyUser({
        ...user,
        firstName: user.first_name,
        lastName: user.last_name,
      });
      trackEvent("login", { method: "otp" });
    }
    // onAuthChanged fires from verifyOtp, which will dispatch INITIALIZE via
    // handleAuthChanged. No need to dispatch here.
  }, []);

  const signOut = useCallback(async () => {
    trackEvent("logout");
    resetUser();
    await authLogout();
    // authLogout fires notifyAuthChanged, which dispatches SIGN_OUT.
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "cookie",
        startLogin,
        completeLogin,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
